import { getLocale, translate } from "../localization";
const ACTIVE_SUBSCRIPTION_TYPES = new Set(["trial", "subscribed", "perpetual"]);
const NORMALIZED_SUBSCRIPTION_SOURCES = {
  stripe: "stripe",
  billing: "stripe",
  meta: "meta",
  manual: "manual",
  perpetual: "manual",
  system: "system",
};

export const normalizeSubscriptionStatus = (status) => {
  if (!status) {
    return null;
  }

  const normalizedStatus = String(status).trim().toLowerCase();

  if (normalizedStatus === "active") {
    return "subscribed";
  }

  if (normalizedStatus === "expired") {
    return "free";
  }

  return normalizedStatus;
};

export const parseSubscriptionDate = (value) => {
  if (!value) {
    return null;
  }

  const parsedDate = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return parsedDate;
};

export const normalizeSubscriptionSource = (source) => {
  if (!source) {
    return null;
  }

  const normalizedSource = String(source).trim().toLowerCase();
  return NORMALIZED_SUBSCRIPTION_SOURCES[normalizedSource] || normalizedSource;
};

export const getSubscriptionState = (
  {
    status,
    source,
    expirationDate,
    renews,
    autoRenew,
    cancelAtPeriodEnd,
  } = {},
  now = new Date(),
) => {
  const normalizedStatus = normalizeSubscriptionStatus(status);
  const normalizedSource = normalizeSubscriptionSource(source);
  const parsedExpirationDate = parseSubscriptionDate(expirationDate);
  const isPerpetual = normalizedStatus === "perpetual";
  const isMissingRequiredExpiration =
    ACTIVE_SUBSCRIPTION_TYPES.has(normalizedStatus) &&
    !isPerpetual &&
    !parsedExpirationDate;
  const isExpired =
    normalizedStatus === "free" ||
    isMissingRequiredExpiration ||
    (!isPerpetual &&
      ACTIVE_SUBSCRIPTION_TYPES.has(normalizedStatus) &&
      parsedExpirationDate &&
      parsedExpirationDate.getTime() < now.getTime());
  const hasActiveSubscription =
    ACTIVE_SUBSCRIPTION_TYPES.has(normalizedStatus) &&
    (isPerpetual || !isExpired);
  const hasActiveRecurringSubscription =
    normalizedStatus === "subscribed" &&
    hasActiveSubscription;
  const effectiveRenews =
    hasActiveRecurringSubscription
      ? typeof renews === "boolean"
        ? renews
        : typeof autoRenew === "boolean"
        ? autoRenew
        : normalizedSource === "stripe"
        ? !Boolean(cancelAtPeriodEnd)
        : normalizedSource === "meta"
        ? true
        : false
      : false;
  const willCancelAtPeriodEnd =
    hasActiveRecurringSubscription &&
    normalizedSource === "stripe" &&
    !effectiveRenews;

  return {
    status: isExpired ? "free" : normalizedStatus,
    source: normalizedSource,
    expirationDate: parsedExpirationDate,
    cancelAtPeriodEnd: willCancelAtPeriodEnd,
    renews: effectiveRenews,
    autoRenew: effectiveRenews,
    hasActiveSubscription,
    isTrial: normalizedStatus === "trial" && hasActiveSubscription,
    isSubscribed:
      (normalizedStatus === "subscribed" || normalizedStatus === "perpetual") &&
      hasActiveSubscription,
    isFree: !hasActiveSubscription,
  };
};

export const formatSubscriptionDate = (value) => {
  const parsedDate = parseSubscriptionDate(value);

  if (!parsedDate) {
    return translate("Unknown");
  }

  return parsedDate.toLocaleDateString(getLocale(), {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const formatRoleName = (roleName) => {
  if (!roleName) {
    return translate("Account holder");
  }

  const planRoles = { educatorsite: translate("Educator Site") };
  if (planRoles[String(roleName).trim().toLowerCase()]) {
    return planRoles[String(roleName).trim().toLowerCase()];
  }

  return roleName
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase())
    .join(" ");
};

export const getRoleDisplayName = ({
  roleName,
  roleId,
  isRoleNameLoading = false,
} = {}) => {
  if (roleName) {
    return formatRoleName(roleName);
  }

  if (roleId && isRoleNameLoading) {
    return translate("Loading role...");
  }

  if (roleId) {
    return translate("Role unavailable");
  }

  return formatRoleName(roleName);
};

export const formatSubscriptionLabel = (status, hasActiveSubscription) => {
  const normalizedStatus = normalizeSubscriptionStatus(status);

  if (normalizedStatus === "trial" && hasActiveSubscription) {
    return translate("Trial");
  }

  if (
    (normalizedStatus === "subscribed" || normalizedStatus === "perpetual") &&
    hasActiveSubscription
  ) {
    return translate("Subscribed");
  }

  return translate("Free");
};
